(() => {
  if (customElements.get('clei-video-player')) return;

class CleiVideoPlayer extends HTMLElement {
  constructor() {
    super();

    this.activeVideo = null;
    this.controlsTimer = null;
    this.initializationFrame = null;
    this.abortController = null;
    this.mobileMedia = window.matchMedia('(max-width: 749px)');
  }

  connectedCallback() {
    if (this.abortController || this.initializationFrame) return;

    this.initializationFrame = window.requestAnimationFrame(() => {
      this.initializationFrame = null;
      if (!this.isConnected) return;
      this.initialize();
    });
  }

  initialize() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.videos = [...this.querySelectorAll('[data-video]')];
    this.frame = this.querySelector('[data-video-frame]');
    this.controls = this.querySelector('[data-video-controls]');
    this.playButton = this.querySelector('[data-video-play]');
    this.muteButton = this.querySelector('[data-video-mute]');
    this.fullscreenButton = this.querySelector('[data-video-fullscreen]');
    this.progress = this.querySelector('[data-video-progress]');
    this.currentTime = this.querySelector('[data-video-current-time]');
    this.duration = this.querySelector('[data-video-duration]');

    if (!this.videos.length || !this.controls) return;

    this.bindEvents();
    this.selectActiveVideo();
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.initializationFrame);
    this.initializationFrame = null;
    this.abortController?.abort();
    this.abortController = null;
    window.clearTimeout(this.controlsTimer);
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.playButton.addEventListener('click', () => this.togglePlayback(), { signal });
    this.muteButton.addEventListener('click', () => this.toggleMute(), { signal });
    this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen(), { signal });
    this.progress.addEventListener('input', () => this.seek(), { signal });

    this.videos.forEach((video) => {
      video.addEventListener('click', () => this.togglePlayback(), { signal });
      video.addEventListener('play', () => this.handleMediaState(video), { signal });
      video.addEventListener('pause', () => this.handleMediaState(video), { signal });
      video.addEventListener('timeupdate', () => this.updateTimeline(video), { signal });
      video.addEventListener('durationchange', () => this.updateTimeline(video), { signal });
      video.addEventListener('volumechange', () => this.updateMuteState(video), { signal });
      video.addEventListener('webkitbeginfullscreen', () => this.updateFullscreenState(), { signal });
      video.addEventListener('webkitendfullscreen', () => this.updateFullscreenState(), { signal });
    });

    this.addEventListener('pointermove', () => this.showControls(), { signal });
    this.addEventListener('pointerleave', () => this.scheduleControls(), { signal });
    this.addEventListener('touchstart', () => this.showControls(), { signal, passive: true });

    document.addEventListener('fullscreenchange', () => this.updateFullscreenState(), { signal });
    document.addEventListener('webkitfullscreenchange', () => this.updateFullscreenState(), { signal });

    this.mobileMedia.addEventListener('change', () => this.selectActiveVideo(), { signal });
  }

  selectActiveVideo() {
    const previousVideo = this.activeVideo;
    const mobileVideo = this.querySelector('[data-video-mobile]');
    const desktopVideo = this.querySelector('[data-video-desktop]');
    const nextVideo = this.mobileMedia.matches && mobileVideo ? mobileVideo : desktopVideo;

    if (!nextVideo || nextVideo === previousVideo) {
      this.updateInterface();
      return;
    }

    const shouldPlay = previousVideo
      ? !previousVideo.paused
      : this.dataset.autoplay === 'true';
    const currentTime = previousVideo?.currentTime || 0;
    const muted = previousVideo?.muted ?? nextVideo.muted;

    previousVideo?.pause();
    this.videos.forEach((video) => {
      if (video !== nextVideo) video.pause();
    });

    this.activeVideo = nextVideo;
    this.activeVideo.muted = muted;
    this.updateInterface();

    const restoreState = () => {
      if (Number.isFinite(this.activeVideo.duration)) {
        this.activeVideo.currentTime = Math.min(currentTime, this.activeVideo.duration);
      }

      if (shouldPlay) {
        this.activeVideo.play().catch(() => {
          this.updateInterface();
        });
      }

      this.updateInterface();
    };

    if (this.activeVideo.readyState >= 1) {
      restoreState();
    } else {
      this.activeVideo.addEventListener('loadedmetadata', restoreState, { once: true });
    }
  }

  togglePlayback() {
    if (!this.activeVideo) return;

    if (this.activeVideo.paused) {
      this.activeVideo.play().catch(() => {
        this.updateInterface();
      });
    } else {
      this.activeVideo.pause();
    }
  }

  toggleMute() {
    if (!this.activeVideo) return;

    const currentlyMuted = this.activeVideo.muted || this.activeVideo.volume === 0;

    if (currentlyMuted && this.activeVideo.volume === 0) {
      this.activeVideo.volume = 1;
    }

    this.activeVideo.muted = !currentlyMuted;
    this.updateMuteState(this.activeVideo);
    this.showControls();
  }

  seek() {
    if (!this.activeVideo || !Number.isFinite(this.activeVideo.duration)) return;

    const position = Number(this.progress.value) / 100;
    this.activeVideo.currentTime = position * this.activeVideo.duration;
    this.updateTimeline(this.activeVideo);
  }

  async toggleFullscreen() {
    if (!this.frame || !this.activeVideo) return;

    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement;

    if (fullscreenElement) {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      return;
    }

    try {
      if (this.frame.requestFullscreen) {
        await this.frame.requestFullscreen();
      } else if (this.frame.webkitRequestFullscreen) {
        this.frame.webkitRequestFullscreen();
      } else if (this.activeVideo.webkitEnterFullscreen) {
        this.activeVideo.webkitEnterFullscreen();
      }
    } catch (error) {
      if (this.activeVideo.webkitEnterFullscreen) {
        this.activeVideo.webkitEnterFullscreen();
      }
    }
  }

  handleMediaState(video) {
    if (video !== this.activeVideo) return;

    this.updatePlaybackState();

    if (video.paused) {
      this.showControls(false);
    } else {
      this.scheduleControls();
    }
  }

  updateInterface() {
    this.updatePlaybackState();
    this.updateMuteState(this.activeVideo);
    this.updateTimeline(this.activeVideo);
    this.updateFullscreenState();
  }

  updatePlaybackState() {
    if (!this.activeVideo) return;

    const paused = this.activeVideo.paused;
    this.classList.toggle('is-paused', paused);
    this.classList.toggle('is-playing', !paused);
    this.playButton.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
  }

  updateMuteState(video) {
    if (!video || video !== this.activeVideo) return;

    const muted = video.muted || video.volume === 0;
    this.classList.toggle('is-muted', muted);
    this.muteButton.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
  }

  updateTimeline(video) {
    if (!video || video !== this.activeVideo) return;

    const validDuration = Number.isFinite(video.duration) && video.duration > 0;
    const percentage = validDuration ? (video.currentTime / video.duration) * 100 : 0;

    this.progress.value = percentage;
    this.progress.style.setProperty('--video-player-progress-position', `${percentage}%`);
    this.currentTime.textContent = this.formatTime(video.currentTime);
    this.duration.textContent = this.formatTime(validDuration ? video.duration : 0);
  }

  updateFullscreenState() {
    if (!this.fullscreenButton) return;

    const active =
      document.fullscreenElement === this.frame ||
      document.webkitFullscreenElement === this.frame ||
      this.activeVideo?.webkitDisplayingFullscreen;

    this.classList.toggle('is-fullscreen', Boolean(active));
    this.fullscreenButton.setAttribute(
      'aria-label',
      active ? 'Exit fullscreen' : 'Enter fullscreen'
    );
  }

  showControls(scheduleHide = true) {
    window.clearTimeout(this.controlsTimer);
    this.classList.add('is-controls-visible');

    if (scheduleHide) this.scheduleControls();
  }

  scheduleControls() {
    window.clearTimeout(this.controlsTimer);

    if (!this.activeVideo || this.activeVideo.paused) return;

    this.controlsTimer = window.setTimeout(() => {
      if (!this.matches(':focus-within')) {
        this.classList.remove('is-controls-visible');
      }
    }, 2500);
  }

  formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}

if (!customElements.get('clei-video-player')) {
  customElements.define('clei-video-player', CleiVideoPlayer);
}
})();
